import type { Dispatch, SetStateAction } from "react"
import { API_BASE_URL } from "../config.ts"
import type { StagedAttachment } from "../types.ts"
import { toast } from "./toast.tsx"

type UploadFilesParams = {
  files: File[]
  agentName: string
  conversationId?: string
  setAttachments: Dispatch<SetStateAction<StagedAttachment[]>>
  onAuthError?: () => void
}

type UploadFilesResult = {
  conversationId?: string
}

export async function uploadFiles({
  files,
  agentName,
  conversationId,
  setAttachments,
  onAuthError
}: UploadFilesParams): Promise<UploadFilesResult> {
  let resultConversationId: string | undefined

  const uploads = files.map(async (file) => {
    const tempId = crypto.randomUUID()
    setAttachments((prev) => [
      ...prev,
      {
        id: tempId,
        name: file.name,
        contentType: file.type || "application/octet-stream",
        sizeBytes: 0,
        status: "uploading"
      }
    ])

    try {
      const params = new URLSearchParams({
        agentName,
        fileName: file.name,
        contentType: file.type || "application/octet-stream"
      })
      if (conversationId) {
        params.set("conversationId", conversationId)
      }

      const response = await fetch(`${API_BASE_URL}/api/attachments/upload?${params}`, {
        method: "POST",
        credentials: "include",
        headers: { "content-type": "application/octet-stream" },
        body: file
      })

      if (response.status === 401) {
        setAttachments((prev) => prev.filter((a) => a.id !== tempId))
        onAuthError?.()
        return
      }

      if (!response.ok) {
        setAttachments((prev) => prev.filter((a) => a.id !== tempId))
        toast.error("Failed to upload file", { description: `Upload returned ${response.status}` })
        return
      }

      const result = (await response.json()) as StagedAttachment & { conversationId?: string }
      if (result.conversationId) {
        resultConversationId = result.conversationId
      }
      setAttachments((prev) =>
        prev.map((a) =>
          a.id === tempId
            ? {
                id: result.id,
                name: result.name,
                contentType: result.contentType,
                sizeBytes: result.sizeBytes,
                status: "ready" as const
              }
            : a
        )
      )
    } catch (error) {
      setAttachments((prev) => prev.filter((a) => a.id !== tempId))
      toast.error("Failed to upload file", { description: error instanceof Error ? error.message : "Unknown error" })
    }
  })

  await Promise.all(uploads)
  return { conversationId: resultConversationId }
}
